"use client";

import { useEffect, useRef, useState } from "react";

interface HeaderProps {
    children: React.ReactNode;
    className?: string;
    scrolledClassName?: string;
    unscrolledClassName?: string;
}

export function Header({
    children,
    className,
    scrolledClassName = "bg-background",
    unscrolledClassName = "bg-background",
}: HeaderProps) {
    const [scrolled, setScrolled] = useState(false);
    const headerRef = useRef<HTMLElement>(null);

    // Track header height in --header-h via ResizeObserver. This reads the
    // height after layout has settled (the observer callback runs post-layout)
    // rather than synchronously during hydration, which previously triggered a
    // forced reflow as the very first useEffect on mount.
    useEffect(() => {
        const header = headerRef.current;
        if (!header) return;
        const observer = new ResizeObserver(([entry]) => {
            document.documentElement.style.setProperty(
                "--header-h",
                `${entry.contentRect.height}px`
            );
        });
        observer.observe(header);
        return () => observer.disconnect();
    }, []);

    // Listen to the document scroll. The previous implementation walked
    // ancestors with getComputedStyle(...) to find a scrollable container —
    // each call forced a sync layout. Window scroll is the actual scroller in
    // this app, so the traversal was 100% wasted reflow cost.
    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 100);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    return (
        <header
            ref={headerRef}
            data-scrolled={scrolled ? "true" : "false"}
            className={[
                "group sticky top-0 z-30 transition-all duration-1000",
                scrolled ? scrolledClassName : unscrolledClassName,
                className,
            ].filter(Boolean).join(" ")}
        >
            {children}
        </header>
    );
}
